import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Calendar, Clock, Eye, MessageSquare } from "lucide-react";
import { useClaudeCode } from "../../context/ClaudeCodeContext";
import type { SessionRow } from "../../utils/types";
import UserCell from "../UserCell";

const formatDate = (s: string | null | undefined) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return s;
  }
};

const formatTime = (s: string | null | undefined) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
};

const truncate = (s: string | null | undefined, n: number): string => {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const sessionTitle = (row: SessionRow): string => {
  const candidate =
    row.first_prompt_text ||
    row.first_prompt ||
    row.top_prompt ||
    row.session_name;
  const trimmed = candidate?.trim();
  if (trimmed) return trimmed;
  return `Session ${row.session_id.slice(0, 8)}`;
};

type SessionRowWithSr = SessionRow & { _srNo: number };

const SessionsTable: React.FC = () => {
  const {
    sessions,
    sessionsLoading,
    sessionsTotal,
    sessionFilters,
    setSessionFilters,
    setSelectedSessionId,
  } = useClaudeCode();

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const totalPages = Math.max(
    1,
    Math.ceil(sessionsTotal / sessionFilters.page_size)
  );

  const accessors = useMemo<
    Record<string, (s: SessionRow) => string | number | null>
  >(
    () => ({
      session_name: (s) => sessionTitle(s).toLowerCase(),
      user_email: (s) => (s.user_name || s.user_email || "").toLowerCase(),
      date: (s) => s.last_prompt_at || "",
      source: (s) => (s.app_name || "").toLowerCase(),
    }),
    []
  );

  const tableData = useMemo<SessionRowWithSr[]>(() => {
    const sorted = sortKey
      ? [...sessions].sort((a, b) => {
          const accessor = accessors[sortKey];
          if (!accessor) return 0;
          const aVal = accessor(a);
          const bVal = accessor(b);
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          const cmp = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
          });
          return sortDir === "desc" ? -cmp : cmp;
        })
      : sessions;
    return sorted.map((s, i) => ({
      ...s,
      _srNo: sessionFilters.page * sessionFilters.page_size + i + 1,
    }));
  }, [
    sessions,
    sortKey,
    sortDir,
    accessors,
    sessionFilters.page,
    sessionFilters.page_size,
  ]);

  const columns = useMemo(
    (): TableColumn<SessionRowWithSr>[] => [
      {
        key: "session_name",
        header: "Session Name",
        accessor: (row) => sessionTitle(row),
        sortable: true,
        width: "40%",
        render: (_, row) => {
          const title = sessionTitle(row);
          return (
            <div className="min-w-0 max-w-full">
              <div
                className="ccd-session-title text-sm font-medium text-gray-900"
                title={title}
              >
                {title}
              </div>
              <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-500">
                <MessageSquare className="h-3 w-3" />
                <span>{row.prompt_count} messages</span>
              </div>
            </div>
          );
        },
      },
      {
        key: "user_email",
        header: "User",
        accessor: (row) => row.user_name || row.user_email,
        sortable: true,
        width: "20%",
        render: (_, row) => (
          <UserCell
            user_email={row.user_email}
            user_name={row.user_name}
            image={row.image}
          />
        ),
      },
      {
        key: "date",
        header: "Date",
        accessor: (row) => row.last_prompt_at,
        sortable: true,
        width: "16%",
        render: (_, row) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              <span>{formatDate(row.last_prompt_at)}</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5">
              <Clock className="h-3 w-3" />
              <span>Updated {formatTime(row.last_prompt_at)}</span>
            </div>
          </div>
        ),
      },
      {
        key: "source",
        header: "Source",
        accessor: (row) => row.app_name || "",
        sortable: true,
        width: "14%",
        align: "center",
        render: (_, row) =>
          row.app_name ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="secondary"
                    className="bg-gray-100 text-gray-700 hover:bg-gray-100 max-w-full truncate"
                  >
                    {truncate(row.app_name, 18)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{row.app_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Badge
              variant="secondary"
              className="bg-gray-50 text-gray-500 hover:bg-gray-50"
            >
              App
            </Badge>
          ),
      },
      {
        key: "view",
        header: "View",
        accessor: () => "",
        width: "80px",
        align: "center",
        render: (_, row) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSessionId(row.session_id);
                  }}
                  className="h-8 w-8 text-[#334238] hover:bg-[#dbf566]/20"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View prompts</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
    ],
    [setSelectedSessionId]
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <DataTable<SessionRowWithSr>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="calc(100vh - 360px)"
        columns={columns}
        data={tableData}
        loading={sessionsLoading}
        emptyMessage="No sessions found."
        stickyHeader
        stickyFooter
        showActions={false}
        onRowClick={(row) => setSelectedSessionId(row.session_id)}
        onSort={(key, direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
        sortConfig={
          sortKey ? { key: sortKey, direction: sortDir } : undefined
        }
        pagination={{
          currentPage: sessionFilters.page + 1,
          totalPages,
          pageSize: sessionFilters.page_size,
          totalItems: sessionsTotal,
          onPageChange: (p) =>
            setSessionFilters({
              ...sessionFilters,
              page: Math.max(0, p - 1),
            }),
          onPageSizeChange: (size) =>
            setSessionFilters({
              ...sessionFilters,
              page_size: size,
              page: 0,
            }),
          pageSizeOptions: [25, 50, 100],
        }}
      />
    </div>
  );
};

export default SessionsTable;
